import { useEffect, useState } from "react";
import api from "@/lib/apiClient";
import { toast } from "sonner";
import { fmtMoney } from "@/lib/paymentConstants";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Download, Printer, Mail, GaugeCircle } from "lucide-react";

function Line({ label, value, strong }) {
  return (
    <div className="flex items-center justify-between py-1.5 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className={strong ? "font-semibold text-secondary" : "text-secondary"}>{value}</span>
    </div>
  );
}

export default function ReceiptDialog({ paymentId, open, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !paymentId) return;
    setLoading(true);
    api.get(`/payments/${paymentId}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [open, paymentId]);

  const getBlob = async () => {
    const res = await api.get(`/payments/${paymentId}/receipt`, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = await getBlob();
      const a = document.createElement("a");
      a.href = url; a.download = `receipt_${data?.payment?.receipt_number || paymentId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
      toast.success("Receipt downloaded");
    } catch { toast.error("Could not generate receipt"); }
    finally { setBusy(false); }
  };

  const print = async () => {
    setBusy(true);
    try {
      const url = await getBlob();
      const win = window.open(url, "_blank");
      if (win) win.addEventListener("load", () => win.print());
    } catch { toast.error("Could not open receipt"); }
    finally { setBusy(false); }
  };

  const email = async () => {
    setBusy(true);
    try {
      await api.post(`/payments/${paymentId}/email-receipt`);
      toast.success("Receipt emailed");
    } catch (err) {
      toast.info(err.response?.data?.detail || "Email delivery is not configured yet. Download the PDF instead.");
    } finally { setBusy(false); }
  };

  const p = data?.payment;
  const sum = data?.student_summary;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle>Payment Receipt</DialogTitle>
          <DialogDescription>{p?.receipt_number}</DialogDescription>
        </DialogHeader>
        {loading || !p ? (
          <div className="flex h-40 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="rounded-xl border border-border">
              <div className="flex items-center gap-2 rounded-t-xl bg-secondary px-4 py-3 text-white">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary"><GaugeCircle className="h-4 w-4" /></span>
                <div><p className="font-display text-sm font-bold">SIFCORE</p><p className="text-[10px] text-slate-300">Payment Receipt</p></div>
                <span className="ml-auto font-mono text-xs text-slate-300">{p.receipt_number}</span>
              </div>
              <div className="px-4 py-3">
                <Line label="Student" value={p.student_name} strong />
                <Line label="Student ID" value={p.student_code} />
                <Line label="Course" value={p.course || "—"} />
                <Line label="Batch" value={p.batch || "—"} />
                <Line label="Date" value={p.payment_date} />
                <Line label="Mode" value={p.payment_mode} />
                {p.transaction_id && <Line label="Transaction ID" value={p.transaction_id} />}
                <div className="my-2 border-t border-dashed border-border" />
                <Line label="Amount Received" value={fmtMoney(p.amount_received)} strong />
                {sum && <Line label="Total Paid" value={fmtMoney(sum.total_paid)} />}
                {sum && <Line label="Remaining Balance" value={fmtMoney(sum.pending_amount)} strong />}
              </div>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              <Button variant="outline" onClick={download} disabled={busy} data-testid="download-receipt"><Download className="mr-1.5 h-4 w-4" /> PDF</Button>
              <Button variant="outline" onClick={print} disabled={busy} data-testid="print-receipt"><Printer className="mr-1.5 h-4 w-4" /> Print</Button>
              <Button variant="outline" onClick={email} disabled={busy} data-testid="email-receipt"><Mail className="mr-1.5 h-4 w-4" /> Email</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
